import React, { useEffect, useState, useMemo, useRef } from 'react';
import { CompetitorBatchConfig, CompetitorData } from '../types';
import { CompetitorCard } from './CompetitorCard';

interface CompetitorGridProps {
  batches: CompetitorBatchConfig[];
  revealedBatches: number[];
  visible: boolean;
  isMini?: boolean;
  analyzed?: boolean;
}

// Estimated width of one expanded card (logo + name + check + padding) + gap
const EXPANDED_CARD_WIDTH = 244;
// Space reserved for collapsed cards (3 × ~44px overlap) + "+more" counter
const COLLAPSED_BUFFER = 220;

export const CompetitorGrid: React.FC<CompetitorGridProps> = ({ batches, visible, isMini, analyzed }) => {
  const [visibleCount, setVisibleCount] = useState(0);
  const [analyzedIds, setAnalyzedIds] = useState<Set<string>>(new Set());
  const [containerWidth, setContainerWidth] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      setContainerWidth(entries[0].contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const flatCompetitors = useMemo(() => {
    return batches.reduce<{ comp: CompetitorData; delay: number; loadingMs: number }[]>((acc, batch) => {
      const batchEnd = batch.delayMs + batch.loadingDurationMs;
      batch.competitors.forEach((c, cIdx) => {
        const delay = batch.delayMs + cIdx * batch.staggerMs;
        acc.push({ comp: c, delay, loadingMs: batchEnd - delay });
      });
      return acc;
    }, []);
  }, [batches]);

  useEffect(() => {
    if (!visible) {
      setVisibleCount(0);
      setAnalyzedIds(new Set());
      return;
    }

    if (isMini) {
      setVisibleCount(flatCompetitors.length);
      setAnalyzedIds(new Set(flatCompetitors.map((i) => i.comp.id)));
      return;
    }

    const timers: NodeJS.Timeout[] = [];
    flatCompetitors.forEach((item, index) => {
      const revealTimer = setTimeout(() => {
        setVisibleCount((prev) => Math.max(prev, index + 1));
      }, item.delay);
      timers.push(revealTimer);

      const analyzedTimer = setTimeout(() => {
        setAnalyzedIds((prev) => {
          if (prev.has(item.comp.id)) return prev;
          const next = new Set(prev);
          next.add(item.comp.id);
          return next;
        });
      }, item.delay + item.loadingMs);
      timers.push(analyzedTimer);
    });

    return () => timers.forEach(clearTimeout);
  }, [visible, flatCompetitors, isMini]);

  const activeCompetitors = useMemo(() => flatCompetitors.slice(0, visibleCount), [flatCompetitors, visibleCount]);
  
  // Newest first (prepend)
  const displayList = [...activeCompetitors].reverse();
  
  const expandedLimit = useMemo(() => {
    if (analyzed || isMini) return 0;
    if (containerWidth === 0) return 2;
    const available = containerWidth - COLLAPSED_BUFFER;
    return Math.max(1, Math.min(4, Math.floor((available + 24) / EXPANDED_CARD_WIDTH)));
  }, [analyzed, isMini, containerWidth]);

  const maxRendered = expandedLimit + 3;
  const hiddenCount = Math.max(0, displayList.length - maxRendered);

  return (
    <div ref={sectionRef} className={`ms-competitors-section${visible ? ' visible' : ''}${isMini ? ' mini' : ''}${analyzed ? ' analyzed' : ''}`}>
      <div className="ms-competitors-header" style={{ marginBottom: isMini ? '12px' : '8px' }}>
        <span className={`ms-competitors-title ${analyzed || isMini ? 'ms-text-white-static' : 'ms-text-silver-shine'}`}>
          {analyzed || isMini ? 'Analyzed competitors' : 'Analyzing competitors ...'}
        </span>
      </div>
      <div className="ms-competitors-list">
        {displayList.slice(0, maxRendered).map((item, localIdx) => {
          const isExpanded = localIdx < expandedLimit;
          const isCollapsed = localIdx >= expandedLimit;
          const analyzed = analyzedIds.has(item.comp.id);
          const isBeaming = !isMini && isExpanded && !analyzed;

          return (
            <CompetitorCard
              key={item.comp.id}
              competitor={item.comp}
              analyzed={analyzed}
              isBeaming={isBeaming}
              isExpanded={isExpanded}
              isCollapsed={isCollapsed}
              isMini={isMini}
              style={{
                zIndex: 40 - localIdx,
                marginLeft: (isCollapsed && localIdx > expandedLimit) ? (isMini ? '-20px' : '-44px') : undefined
              }}
            />
          );  
        })}
        {hiddenCount > 0 && (
          <div className="ms-more-counter visible" style={isMini ? { fontSize: '12px' } : undefined}>
            +{hiddenCount} more
          </div>
        )}
      </div>
    </div>
  );
};
